import React from 'react';
import { Platform, View } from 'react-native';
import { Image as ExpoImage } from 'expo-image';
import { useTranslation } from 'react-i18next';

import { ContextSheet } from '@/components/ui/ContextSheet';
import { Button, ButtonText } from '@/components/gluestack/button';
import { Spinner } from '@/components/gluestack/spinner';
import { Textarea, TextareaInput } from '@/components/gluestack/textarea';
import { PhotoPolicyNotice } from '@/components/domain/common/PhotoPolicyNotice';
import { useIsLargeScreen } from '@/hooks/useIsLargeScreen';

interface IImagePreviewSheetProps {
  imageUri: string;
  onConfirm: () => void;
  onCancel: () => void;
  isUploading?: boolean;
  title?: string;
  caption?: string;
  onCaptionChange?: (text: string) => void;
  showCaption?: boolean;
  showPolicyNotice?: boolean;
}

const ImagePreviewContent = ({ payload }: { payload?: any }) => {
  const { t } = useTranslation();
  const isLargeScreen = useIsLargeScreen();

  const {
    imageUri,
    onConfirm,
    onCancel,
    isUploading,
    caption,
    onCaptionChange,
    showCaption = true,
    showPolicyNotice = false,
  } = payload;

  return (
    <View className="flex-1 gap-3 pb-2">
      <View className="min-h-[80px] flex-1 overflow-hidden rounded-xl bg-background-0 p-2">
        {Platform.OS === 'web' ? (
          <img
            src={imageUri}
            alt="Preview"
            className="h-full w-full object-contain"
          />
        ) : (
          <ExpoImage
            source={{ uri: imageUri }}
            alt="Preview"
            className="h-full w-full"
            contentFit="contain"
          />
        )}
      </View>

      {showCaption && (
        <Textarea className="h-24 min-h-10 shrink rounded-lg px-3 py-2">
          <TextareaInput
            value={caption}
            onChangeText={onCaptionChange}
            placeholder={t('photos.captionPlaceholder', 'Add a caption (optional)')}
            className="m-0 p-0"
            textAlignVertical="top"
            autoFocus={Platform.OS === 'web' && isLargeScreen}
          />
        </Textarea>
      )}

      {showPolicyNotice && (
        <View>
          <PhotoPolicyNotice />
        </View>
      )}

      <View className="shrink-0 flex-row justify-center gap-3 pt-2">
        <Button variant="outline" action="primary" onPress={onCancel} isDisabled={isUploading}>
          <ButtonText>{t('messages.cancel', 'Cancel')}</ButtonText>
        </Button>
        <Button onPress={onConfirm} isDisabled={isUploading}>
          {isUploading && <Spinner size="small" className="mr-2" color="white" />}
          <ButtonText>{t('messages.confirm', 'Confirm')}</ButtonText>
        </Button>
      </View>
    </View>
  );
};

export function ImagePreviewSheet({
  imageUri,
  onConfirm,
  onCancel,
  isUploading = false,
  title,
  caption,
  onCaptionChange,
  showCaption = true,
  showPolicyNotice = false,
}: IImagePreviewSheetProps) {
  const { t } = useTranslation();

  return (
    <ContextSheet
      largeScreenMode="modal"
      isOpen={!!imageUri}
      onClose={onCancel}
      title={title || t('messages.uploadPhoto', 'Upload Image')}
      height="xl"
      contentComponent={ImagePreviewContent}
      payload={{
        imageUri,
        onConfirm,
        onCancel,
        isUploading,
        caption,
        onCaptionChange,
        showCaption,
        showPolicyNotice,
      }}
    />
  );
}
